import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Eye,
  Download,
  FileText,
  Search,
  Copy,
  CalendarIcon,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parse } from "date-fns";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ServiceScript, ServiceScriptFilters, Tag } from "@/types/serviceScript";
import {
  getServiceScripts,
  deleteServiceScript,
  toggleServiceScriptStatus,
  getServiceScriptTags,
} from "@/Api/serviceScripts";
import { getSelectContests } from "@/Api/contests";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { AddServiceScript } from "../Add";
import { UpdateServiceScript } from "../Update";
import { ShowServiceScript } from "../Show";
import { Main } from "@/components/Main";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { useTypeUser } from "@/hooks/auth";

interface TableDataProps extends ServiceScript {}

interface ExtendedPageActions extends TypePageActions {
  refresh?: () => Promise<void>;
}

interface Lottery {
  id: number;
  name: string;
  color: string;
}

interface Contest {
  id: number;
  uuid: string;
  name: string;
  lottery?: Lottery;
}

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
  onEditScript,
  onViewScript,
}: {
  dataApi: ApiDataProps<TableDataProps>;
  pageActions: ExtendedPageActions;
  updateFront: any;
  onEditScript: (uuid: string) => void;
  onViewScript: (uuid: string) => void;
}) => {
  const { hasPermission } = usePermissions();
  const isSeller = useTypeUser(3);
  const canEdit = !isSeller(() => true) && hasPermission("edit-service-script");
  const canDelete = !isSeller(() => true) && hasPermission("delete-service-script");

  const handleToggleStatus = async (uuid: string) => {
    try {
      const response = await toggleServiceScriptStatus(uuid);
      if (response.status === 200) {
        toast.success("Status atualizado com sucesso!");
        if (pageActions.refresh) {
          await pageActions.refresh();
        }
      } else {
        toast.error("Erro ao atualizar status");
      }
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasAttachments = (script: ServiceScript) => {
    return script.attachments && script.attachments.length > 0;
  };

  const getPlainText = (html: string) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const handleCopyContent = async (content: string) => {
    // Converte HTML para texto preservando quebras de linha (mesma lógica do modal)
    let text = content;

    // Substitui tags de quebra de linha por \n
    text = text.replace(/<br\s*\/?>/gi, "\n");
    text = text.replace(/<\/p>/gi, "\n");
    text = text.replace(/<\/div>/gi, "\n");
    text = text.replace(/<\/li>/gi, "\n");

    // Remove todas as outras tags HTML
    text = text.replace(/<[^>]+>/g, "");

    // Decodifica entidades HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    text = tempDiv.textContent || tempDiv.innerText || "";

    // Remove espaços extras no início/fim de cada linha e linhas vazias consecutivas
    text = text
      .split("\n")
      .map(line => line.trim())
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    try {
      await navigator.clipboard.writeText(text);
      toast.success("Conteúdo copiado!");
    } catch (error) {
      toast.error("Erro ao copiar");
    }
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader className="bg-primary/10 dark:bg-primary/20">
          <TableRow>
            <TableHead className="text-primary font-semibold">Título</TableHead>
            <TableHead className="text-primary font-semibold">Concurso</TableHead>
            <TableHead className="text-primary font-semibold">Tags</TableHead>
            <TableHead className="text-primary font-semibold">Conteúdo</TableHead>
            <TableHead className="text-center text-primary font-semibold">Ativo</TableHead>
            <TableHead className="text-primary font-semibold">Data Cadastro</TableHead>
            <TableHead className="text-center text-primary font-semibold">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((item) => (
            <TableRow key={item?.id}>
              <TableCell className="font-medium max-w-[150px]">
                <span className="block truncate" title={item?.title}>
                  {item?.title}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  {item?.contest ? (
                    <>
                      <span
                        className="px-2 py-0.5 text-xs rounded-full text-center w-fit font-medium border-2 bg-white"
                        style={{
                          borderColor: item.contest.lottery?.color || '#023859',
                          color: item.contest.lottery?.color || '#023859'
                        }}
                      >
                        {item.contest.name}
                      </span>
                      {item.contest.lottery && (
                        <span
                          className="px-1.5 py-0.5 text-[10px] rounded-full text-white text-center w-fit"
                          style={{ backgroundColor: item.contest.lottery.color }}
                        >
                          {item.contest.lottery.name}
                        </span>
                      )}
                    </>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Atemporal</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1 max-w-[120px]">
                  {item?.tags?.slice(0, 2).map((tag) => (
                    <span
                      key={tag.id}
                      className="px-1.5 py-0.5 text-[10px] rounded-full text-white"
                      style={{ backgroundColor: tag.color || "#6b7280" }}
                    >
                      {tag.name}
                    </span>
                  ))}
                  {item?.tags?.length > 2 && (
                    <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-gray-500 text-white">
                      +{item.tags.length - 2}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="max-w-[250px]">
                <TooltipProvider>
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <div
                        className="flex items-center gap-1 cursor-pointer hover:bg-muted/50 rounded px-2 py-1 -mx-2 -my-1 transition-colors"
                        onClick={() => handleCopyContent(item?.content)}
                      >
                        <span className="text-sm text-muted-foreground truncate">
                          {getPlainText(item?.content).substring(0, 40)}
                          {getPlainText(item?.content).length > 40 && "..."}
                        </span>
                        <Copy className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground ml-1" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="max-w-[400px] max-h-[300px] overflow-y-auto p-4 bg-popover border shadow-lg"
                    >
                      <div
                        className="prose prose-sm dark:prose-invert"
                        dangerouslySetInnerHTML={{ __html: item?.content }}
                      />
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
              <If condition={canEdit}>
                <TableCell className="text-center">
                  <Switch
                    checked={item?.is_active}
                    onCheckedChange={() => handleToggleStatus(item?.uuid)}
                  />
                </TableCell>
              </If>
              <If condition={!canEdit}>
                <TableCell className="text-center">
                  <Badge variant={item?.is_active ? "default" : "secondary"}>
                    {item?.is_active ? "Sim" : "Não"}
                  </Badge>
                </TableCell>
              </If>
              <TableCell className="text-sm">{formatDate(item?.created_at)}</TableCell>
              <TableCell>
                <div className="flex gap-2 justify-center">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onViewScript(item?.uuid)}
                    className="h-8 w-8 p-0"
                    title="Visualizar"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {hasAttachments(item) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onViewScript(item?.uuid)}
                      className="h-8 w-8 p-0"
                      title="Tem anexos"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                  <If condition={canEdit}>
                    <Button
                      size="sm"
                      onClick={() => onEditScript(item?.uuid)}
                      className="h-8 w-8 p-0 bg-blue-500 hover:bg-blue-600 text-white"
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </If>
                  <If condition={canDelete}>
                    <DeleteBtn
                      params={{
                        func: deleteServiceScript,
                        funcParams: item?.uuid,
                      }}
                      updateFront={() =>
                        updateFront((prev: any) => ({
                          ...prev,
                          data: [
                            ...prev?.data?.filter(
                              (data: any) => data?.uuid !== item?.uuid
                            ),
                          ],
                        }))
                      }
                      referenceLabel={item?.title}
                    />
                  </If>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {(!dataApi?.data || dataApi?.data?.length === 0) && (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8">
                Nenhum script encontrado
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};

export function ServiceScriptsList() {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const [filters, setFilters] = useState<ServiceScriptFilters>({});
  const [contests, setContests] = useState<Contest[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [editScriptUuid, setEditScriptUuid] = useState<string | null>(null);
  const [viewScriptUuid, setViewScriptUuid] = useState<string | null>(null);
  const [searchTitle, setSearchTitle] = useState("");
  const { hasPermission } = usePermissions();
  const isSeller = useTypeUser(3);
  const canCreate = !isSeller(() => true) && hasPermission("create-service-script");
  const canEdit = !isSeller(() => true) && hasPermission("edit-service-script");

  const fetchData = async (page: number = 1, currentFilters = filters) => {
    setState("loading");
    const { data } = await getServiceScripts(page, currentFilters);
    setListData(data?.response);
    setState("production");
  };

  const loadFiltersData = async () => {
    try {
      const [contestsRes, tagsRes] = await Promise.all([
        getSelectContests(),
        getServiceScriptTags(),
      ]);
      if (contestsRes.data?.response) {
        setContests(contestsRes.data.response);
      }
      if (tagsRes.data?.response) {
        setTags(tagsRes.data.response);
      }
    } catch (error) {
      console.error("Erro ao carregar filtros:", error);
    }
  };

  useEffect(() => {
    fetchData(1, filters);
    loadFiltersData();
  }, []);

  const handleSearch = async () => {
    const newFilters = { ...filters, title: searchTitle || undefined };
    setFilters(newFilters);
    await fetchData(1, newFilters);
  };

  const handleContestFilter = async (value: string) => {
    let contestId: number | string | undefined = undefined;
    if (value === "atemporal") {
      contestId = "null";
    } else if (value !== "all") {
      contestId = parseInt(value);
    }
    const newFilters = { ...filters, contest_id: contestId };
    setFilters(newFilters);
    await fetchData(1, newFilters);
  };

  const handleTagFilter = async (value: string) => {
    const tagId = value === "all" ? undefined : parseInt(value);
    const newFilters = { ...filters, tag_id: tagId };
    setFilters(newFilters);
    await fetchData(1, newFilters);
  };

  const handleDateFilter = async (field: "date_from" | "date_to", value: string) => {
    const newFilters = { ...filters, [field]: value || undefined };
    setFilters(newFilters);
    await fetchData(1, newFilters);
  };

  const pageActions: ExtendedPageActions = {
    refresh: async () => {
      await fetchData(listData?.current_page || 1, filters);
    },
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchData(listData?.current_page + 1, filters);
      }
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchData(listData?.current_page - 1, filters);
      }
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchData(listData?.last_page, filters);
      }
    },
  };

  const handleEditScript = (uuid: string) => {
    setEditScriptUuid(uuid);
  };

  const handleViewScript = (uuid: string) => {
    setViewScriptUuid(uuid);
  };

  const handleCloseEdit = () => {
    setEditScriptUuid(null);
  };

  const handleCloseView = () => {
    setViewScriptUuid(null);
  };

  return (
    <Main>
      <PageHeader
        icon={FileText}
        title="Scripts de Atendimento"
        description="Gerencie os scripts para atendimento ao cliente"
      >
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => pageActions.refresh?.()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <If condition={canCreate}>
            <AddServiceScript
              updateFront={setListData}
              dataApi={listData}
              refreshList={pageActions.refresh}
            />
          </If>
        </div>
      </PageHeader>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm mb-4">
        {/* Busca por titulo */}
        <div className="flex items-center gap-2">
          <Input
            placeholder="Buscar por título..."
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="w-[200px]"
          />
          <Button size="sm" onClick={handleSearch}>
            <Search className="h-4 w-4" />
          </Button>
        </div>

        {/* Filtro por concurso */}
        <Select onValueChange={handleContestFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar por concurso" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="atemporal">Atemporal</SelectItem>
            {contests.map((contest) => (
              <SelectItem key={contest.id} value={contest.id.toString()}>
                <div className="flex items-center gap-2">
                  <span>{contest.name}</span>
                  {contest.lottery && (
                    <span
                      className="px-2 py-0.5 text-xs rounded-full text-white"
                      style={{ backgroundColor: contest.lottery.color }}
                    >
                      {contest.lottery.name}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtro por tag */}
        <Select onValueChange={handleTagFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar por tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {tags.map((tag) => (
              <SelectItem key={tag.id} value={tag.id.toString()}>
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: tag.color || "#6b7280" }}
                  />
                  {tag.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtro por período */}
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex items-center h-10 w-[150px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
                  filters.date_from ? "text-foreground" : "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.date_from
                  ? format(parse(filters.date_from, "yyyy-MM-dd", new Date()), "dd/MM/yyyy")
                  : "Data inicial"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filters.date_from ? parse(filters.date_from, "yyyy-MM-dd", new Date()) : undefined}
                onSelect={(date) => handleDateFilter("date_from", date ? format(date, "yyyy-MM-dd") : "")}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <span className="text-muted-foreground text-sm">até</span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex items-center h-10 w-[150px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
                  filters.date_to ? "text-foreground" : "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.date_to
                  ? format(parse(filters.date_to, "yyyy-MM-dd", new Date()), "dd/MM/yyyy")
                  : "Data final"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filters.date_to ? parse(filters.date_to, "yyyy-MM-dd", new Date()) : undefined}
                onSelect={(date) => handleDateFilter("date_to", date ? format(date, "yyyy-MM-dd") : "")}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {state === "loading" ? (
          <CreateLoaderTable cols={6} lines={6} />
        ) : (
          <TableData
            dataApi={listData}
            pageActions={pageActions}
            updateFront={setListData}
            onEditScript={handleEditScript}
            onViewScript={handleViewScript}
          />
        )}
      </div>

      {editScriptUuid && canEdit && (
        <UpdateServiceScript
          uuid={editScriptUuid}
          isOpen={!!editScriptUuid}
          onClose={handleCloseEdit}
          onUpdate={pageActions.refresh}
        />
      )}

      {viewScriptUuid && (
        <ShowServiceScript
          uuid={viewScriptUuid}
          isOpen={!!viewScriptUuid}
          onClose={handleCloseView}
        />
      )}
    </Main>
  );
}
